import React from 'react';
import withShipment from '../../../withShipment';
import { MANAGE_MACHINE, SAVE_MACHINE } from './constants';

import { IconButton, TextField } from '@material-ui/core';
import SearchableTable from '../../global/Search/SearchableTable';
import SaveIcon from '@material-ui/icons/Save';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';

import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import {
  editMachine,
  listMachine,
  loadStartedSession,
} from '../../../redux/actions/settings';
import { userInfoSelector } from '../../../redux/selectors/auth';
import {
  loadStartedSessionSelector,
  machineSelector,
} from '../../../redux/selectors/settings';
import ActiveSessionLockBanner from '../../global/ActiveSessionLockBanner';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import { clearStatus } from '../../../redux/actions/global';
import './ManageMachine.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import { Tooltip } from '@mui/material';

class ManageMachine extends SearchableTable {
  constructor(props) {
    super(props);
    this.state = {
      machines: this.props.machines || [],
      // machineIds the user explicitly removed via the row Delete button.
      // Only these are deleted server-side — never a row that is merely
      // absent from the payload (which can happen with stale/partial state).
      deletedMachineIds: [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      showDeleteDialog: false,
      rowToDelete: null,
      rangeStart: '',
      rangeEnd: '',
    };
  }

  componentDidMount() {
    this.props.listMachine(this.props.userInfo.selectedLocationId);
    this.props.loadStartedSession({
      locationId: this.props.userInfo.selectedLocationId,
      isAssignMachine: true,
    });
    this.pushMenuItems();
  }

  isLocked = () => {
    const s = this.props.startedSession;
    return !!(s && s.sessionStarted);
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    // When the active location changes, clear stale local edits and refetch.
    // Otherwise this component could keep one location's machine list in state
    // and save it against a different location's id — wiping the other location.
    if (
      prevProps.userInfo.selectedLocationId !==
      this.props.userInfo.selectedLocationId
    ) {
      this.setState({ machines: [], deletedMachineIds: [] });
      this.props.listMachine(this.props.userInfo.selectedLocationId);
      this.props.loadStartedSession({
        locationId: this.props.userInfo.selectedLocationId,
        isAssignMachine: true,
      });
      return;
    }
    if (prevProps.machines !== this.props.machines) {
      if (this.props.machines && this.props.machines.length > 0) {
        this.setState({ machines: [...this.props.machines] });
      }
    }
    if (prevState.machines !== this.state.machines) {
      this.pushMenuItems();
    }
  }

  pushMenuItems = () => {
    if (this.props.onMenuItemsChange) {
      this.props.onMenuItemsChange(this.getContextualMenuItems());
    }
  };

  getContextualMenuItems = () => [
    {
      title: SAVE_MACHINE,
      icon: <SaveIcon />,
      onClick: this.handleSave,
      disabled: this.isSaveDisabled(),
    },
  ];

  handleSave = () => {
    const machineNames = this.state.machines.map((m) =>
      (m.machine || '').trim().toLowerCase()
    );
    const hasDuplicates = new Set(machineNames).size !== machineNames.length;

    if (hasDuplicates) {
      this.openFeedbackBar('error', 'Duplicate machine names are not allowed.');
      return;
    }

    const callback = (severity, message) => {
      if (severity) {
        this.openFeedbackBar('error', message || 'Failed to save machines');
      } else {
        // Deletions have been applied server-side; reset the pending list so a
        // later save doesn't re-send stale IDs.
        this.setState({ deletedMachineIds: [] });
        this.openFeedbackBar('success', message || 'Saved');
      }
    };

    this.props.editMachine(
      this.state.machines,
      this.props.userInfo.selectedLocationId,
      this.state.deletedMachineIds,
      callback
    );
  };

  isSaveDisabled = () => {
    return (
      this.isLocked() ||
      this.state.machines.some((template) => !template.machine)
    );
  };

  addTemplateRow = () => {
    this.setState((prevState) => {
      const updatedMachines = [
        ...prevState.machines,
        {
          machine: '',
        },
      ];
      return { machines: updatedMachines };
    });
  };
  removeTemplateRow = (index) => {
    this.setState((prevState) => {
      const removed = prevState.machines[index];
      const updatedMachines = prevState.machines.filter(
        (_, curIndex) => curIndex !== index
      );
      // Record the deletion of an already-saved row so the server deletes it
      // explicitly. New (unsaved) rows have no machineId — nothing to delete.
      const updatedDeleted =
        removed && removed.machineId
          ? [...prevState.deletedMachineIds, removed.machineId]
          : prevState.deletedMachineIds;
      return { machines: updatedMachines, deletedMachineIds: updatedDeleted };
    });
  };

  handleTemplateChange = (e, index) => {
    const { name, value } = e.target;
    this.setState((prevState) => {
      const updatedMachines = [...prevState.machines];
      updatedMachines[index] = {
        ...updatedMachines[index],
        [name]: value,
      };

      return { machines: updatedMachines };
    });
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  handleRangeChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  generateMachines = () => {
    const { rangeStart, rangeEnd, machines } = this.state;
    const start = parseInt(rangeStart, 10);
    const end = parseInt(rangeEnd, 10);

    if (isNaN(start) || isNaN(end) || start > end) {
      this.openFeedbackBar('error', 'Invalid range');
      return;
    }

    const existingNames = new Set(
      machines.map((m) => (m.machine || '').toLowerCase())
    );
    const newMachines = [];

    for (let i = start; i <= end; i++) {
      const machineName = `${i}`;
      if (!existingNames.has(machineName.toLowerCase())) {
        newMachines.push({ machine: machineName });
        existingNames.add(machineName.toLowerCase()); // Add immediately to set
      }
    }

    if (newMachines.length === 0) {
      this.openFeedbackBar(
        'error',
        'All machines in the selected range already exist.'
      );
      return;
    }

    const updatedMachines = [...machines, ...newMachines];

    // Sort by number ascending
    updatedMachines.sort(
      (a, b) => parseInt(a.machine, 10) - parseInt(b.machine, 10)
    );

    this.setState({
      machines: updatedMachines,
      rangeStart: '',
      rangeEnd: '',
    });
  };

  render() {
    const { machines } = this.state;

    return (
      <React.Fragment>
        {!this.props.embedded && (
          <TopToolbar
            pageName={MANAGE_MACHINE}
            menuItems={this.getContextualMenuItems()}
          />
        )}

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        {this.isLocked() && (
          <ActiveSessionLockBanner
            sessionId={this.props.startedSession?.sessionId}
          />
        )}

        <Dialog
          open={this.state.showDeleteDialog}
          onClose={() => this.setState({ showDeleteDialog: false })}
        >
          <DialogTitle>{'Confirm Delete'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this machine?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => this.setState({ showDeleteDialog: false })}
              style={{
                width: '100px',
                marginRight: '16px',
                marginBottom: '16px',
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                this.removeTemplateRow(this.state.rowToDelete);
                this.setState({
                  showDeleteDialog: false,
                  rowToDelete: null,
                });
              }}
              color="primary"
              style={{
                width: '100px',
                marginRight: '16px',
                marginBottom: '16px',
              }}
            >
              {' '}
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <div className="manage-machine-range">
          <TextField
            variant="outlined"
            label="Start Range"
            name="rangeStart"
            InputLabelProps={{ shrink: true }}
            value={this.state.rangeStart}
            onChange={this.handleRangeChange}
            type="number"
            className="manage-machine-input"
          />
          <TextField
            variant="outlined"
            label="End Range"
            name="rangeEnd"
            InputLabelProps={{ shrink: true }}
            value={this.state.rangeEnd}
            onChange={this.handleRangeChange}
            type="number"
            className="manage-machine-input"
          />
          <Button
            className="manage-machine-button"
            variant="contained"
            color="primary"
            onClick={this.generateMachines}
            disabled={!this.state.rangeStart || !this.state.rangeEnd}
          >
            Generate Machines
          </Button>
        </div>

        <div className="manage-machine-list">
          <div>
            {machines.map((template, index) => (
              <div className="row-manage-card" key={index}>
                <div className="remove-icon-wrapper">
                  <IconButton
                    onClick={() =>
                      this.setState({
                        showDeleteDialog: true,
                        rowToDelete: index,
                      })
                    }
                  >
                    <RemoveCircleIcon fontSize="medium" color="error" />
                  </IconButton>
                </div>

                <TextField
                  variant="outlined"
                  name="machine"
                  label="Machine"
                  InputLabelProps={{ shrink: true }}
                  value={template.machine || ''}
                  onChange={(e) =>
                    this.handleTemplateChange(
                      {
                        target: {
                          name: e.target.name,
                          value: e.target.value,
                        },
                      },
                      index
                    )
                  }
                  required
                  error={
                    !template.machine ||
                    this.state.machines.some(
                      (m, i) => m.machine === template.machine && i !== index
                    )
                  }
                  helperText={
                    !template.machine
                      ? ''
                      : this.state.machines.some(
                          (m, i) =>
                            m.machine === template.machine && i !== index
                        )
                      ? 'Duplicate machine'
                      : ''
                  }
                />
              </div>
            ))}
            <div className="add-machine-btn-wrapper">
              <Tooltip title={'Add machine'}>
                <IconButton onClick={this.addTemplateRow}>
                  <AddCircleIcon fontSize="medium" color="secondary" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  machines: machineSelector(state),
  startedSession: loadStartedSessionSelector(state),
});

const actionCreators = {
  listMachine,
  editMachine,
  clearStatus,
  loadStartedSession,
};
export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  ManageMachine
);
